import { useCallback } from 'react';
import { compareVersions } from 'compare-versions';
import { bridge, isWeb } from 'shared/src/utils';
import { useMoveToWebview } from 'shared/src/utils/navigator/useCommonNavigator/moveToWebview';
import { useOpenDeeplink, useAppVersion, useThrottle } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';
import { useAppDownloadModal } from 'shared/src/component';
import type {
  GetMumulLiveCurrentCampaignResponse,
  MumulLiveProfileStatus,
} from 'shared/src/api';
import { useLogMumulLiveProfileBadge } from './useLogMumulLiveProfileBadge';

/** 무물라이브 스토리 조회가 가능한 최소 앱 버전 */
const MIN_MUMUL_LIVE_STORY_VERSION = '2.371.0';

type Params = {
  /** market.mumul_live_profile_sno */
  profileSno: number | null;
  profileName: string;
  status: MumulLiveProfileStatus | undefined;
  /** 현재 캠페인을 다시 조회한다 (LIVE 클릭 시 최신값 확인용). */
  refetchCurrentCampaign: () => Promise<{
    data?: GetMumulLiveCurrentCampaignResponse;
  }>;
  /** campaign 이 없는 엣지 케이스(방송 종료)에서 상태를 다시 조회한다. */
  refetchStatus: () => void;
};

/**
 * 상품상세 무물라이브 프로필 클릭 시 상태별 액션. (마켓홈 #2039 와 동일 정책)
 * - LIVE: 현재 캠페인 조회 후 플레이어로 랜딩 (웹: 페이지 / 앱: 딥링크)
 * - HAS_UNREAD_STORY / ALL_READ_STORY: 스토리 조회로 랜딩
 *   (웹: 앱 설치 유도 / 구버전: 업데이트 토스트 / 비회원: 로그인 유도)
 * - DEFAULT: 아무 동작 없음
 */
export const useOnPressMumulLiveProfile = ({
  profileSno,
  profileName,
  status,
  refetchCurrentCampaign,
  refetchStatus,
}: Params) => {
  const { openDeeplink } = useOpenDeeplink();
  const moveToWebview = useMoveToWebview();
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const appVersion = useAppVersion();
  const { data: myInfo } = useMyInfo();
  const { logClickLiveProfileBadge } = useLogMumulLiveProfileBadge();

  // LIVE: 클릭 시점 최신 캠페인 확인 후 플레이어로 랜딩
  const handlePressLive = useCallback(
    async (targetProfileSno: number) => {
      const { data } = await refetchCurrentCampaign();
      const campaign = data?.campaign ?? null;

      logClickLiveProfileBadge({
        profileSno: targetProfileSno,
        profileName,
        status: 'LIVE',
        campaignSno: campaign?.sno ?? null,
      });

      // 엣지 케이스: 조회 시점에 방송이 종료된 경우
      if (campaign == null) {
        bridge.toast('라이브 방송이 종료됐어요');
        refetchStatus();
        return;
      }

      if (isWeb) {
        moveToWebview(`mumul-live/player/${campaign.sno}`);
      } else {
        openDeeplink(`ably://mumul-live/player/${campaign.sno}`);
      }
    },
    [
      logClickLiveProfileBadge,
      profileName,
      refetchCurrentCampaign,
      refetchStatus,
      moveToWebview,
      openDeeplink,
    ],
  );

  // 스토리: 웹=앱 설치 유도 / 구버전=업데이트 토스트 / 비회원=로그인 유도 / 그 외=딥링크
  const handlePressStory = useCallback(
    (targetProfileSno: number, storyStatus: MumulLiveProfileStatus) => {
      logClickLiveProfileBadge({
        profileSno: targetProfileSno,
        profileName,
        status: storyStatus,
        campaignSno: null,
      });

      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      if (appVersion == null) return;
      if (compareVersions(appVersion, MIN_MUMUL_LIVE_STORY_VERSION) < 0) {
        bridge.toast('앱 업데이트가 필요합니다');
        return;
      }
      const storyDeeplink = `ably://mumul-live/stories?profile_sno=${targetProfileSno}`;
      if (myInfo?.is_anonymous) {
        bridge.assertLogin({ returnDeeplink: storyDeeplink });
        return;
      }
      openDeeplink(storyDeeplink);
    },
    [
      logClickLiveProfileBadge,
      profileName,
      showAppDownloadModal,
      appVersion,
      myInfo,
      openDeeplink,
    ],
  );

  const handlePress = useCallback(async () => {
    if (profileSno == null) return;

    if (status === 'LIVE') {
      await handlePressLive(profileSno);
      return;
    }

    if (status === 'HAS_UNREAD_STORY' || status === 'ALL_READ_STORY') {
      handlePressStory(profileSno, status);
      return;
    }

    // DEFAULT: 아무 동작 없음
  }, [profileSno, status, handlePressLive, handlePressStory]);

  // LIVE 는 클릭 시 캠페인 재조회(async) 후 딥링크로 랜딩한다. 연타 시 플레이어가
  // 중복으로 열릴 수 있어 네비게이션 클릭 표준(useThrottle, 네이티브 동일)으로 감싼다.
  return useThrottle(handlePress);
};
